// src/components/shared/CelebrationAnimation.jsx
import React, { useEffect, useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import confetti from 'canvas-confetti';
import { Trophy, Star, Sparkles, Heart, Crown, Award, Zap, CheckCircle } from 'lucide-react';

const CelebrationAnimation = ({
  type = 'default',
  title = 'Congratulations!',
  message = 'You did it!',
  icon = null,
  duration = 3000,
  onComplete = () => {},
  show = true,
  confettiConfig = null,
  points = null,
}) => {
  const [isVisible, setIsVisible] = useState(show);

  useEffect(() => {
    setIsVisible(show);
  }, [show]);

  useEffect(() => {
    if (!isVisible) return;

    // Trigger confetti based on celebration type
    const triggerConfetti = () => {
      const defaultConfig = {
        particleCount: 100,
        spread: 70,
        origin: { y: 0.6 },
        colors: ['#10b981', '#06b6d4', '#a855f7', '#f59e0b', '#ef4444'],
      };

      const configs = {
        section: {
          particleCount: 50,
          spread: 45,
          origin: { y: 0.7 },
        },
        module: {
          particleCount: 150,
          spread: 100,
          origin: { y: 0.5 },
          startVelocity: 45,
        },
        assessment: {
          particleCount: 200,
          spread: 120,
          origin: { y: 0.4 },
          startVelocity: 50,
          gravity: 0.5,
        },
        achievement: {
          particleCount: 100,
          spread: 60,
          origin: { x: 0.5, y: 0.5 },
          shapes: ['star'],
          colors: ['#fbbf24', '#f59e0b', '#eab308'],
        },
        levelup: {
          particleCount: 300,
          spread: 180,
          origin: { y: 0.3 },
          startVelocity: 60,
          gravity: 0.3,
        },
      };

      const config = { ...defaultConfig, ...(configs[type] || {}), ...(confettiConfig || {}) };

      // Multiple bursts for bigger celebrations
      if (type === 'module' || type === 'assessment' || type === 'levelup') {
        confetti(config);
        setTimeout(() => confetti({ ...config, origin: { x: 0.2, y: 0.6 } }), 200);
        setTimeout(() => confetti({ ...config, origin: { x: 0.8, y: 0.6 } }), 400);
      } else {
        confetti(config);
      }
    };

    triggerConfetti();

    // Auto-hide after duration
    const timer = setTimeout(() => {
      setIsVisible(false);
      onComplete();
    }, duration);

    return () => clearTimeout(timer);
  }, [isVisible, type, duration, onComplete, confettiConfig]);

  // Choose icon based on type
  const getIcon = () => {
    if (icon) return icon;

    const icons = {
      section: CheckCircle,
      module: Trophy,
      assessment: Crown,
      achievement: Award,
      levelup: Star,
      streak: Sparkles,
      social: Heart,
      points: Zap,
      default: Star,
    };

    const IconComponent = icons[type] || icons.default;
    return <IconComponent className="w-12 h-12 text-white" />;
  };

  // Choose colors based on type
  const getGradient = () => {
    const gradients = {
      section: 'from-green-500 to-emerald-500',
      module: 'from-purple-500 to-pink-500',
      assessment: 'from-teal-500 to-cyan-500',
      achievement: 'from-yellow-500 to-orange-500',
      levelup: 'from-indigo-500 to-purple-500',
      streak: 'from-red-500 to-pink-500',
      social: 'from-pink-500 to-rose-500',
      points: 'from-yellow-500 to-amber-500',
      default: 'from-blue-500 to-purple-500',
    };

    return gradients[type] || gradients.default;
  };

  return (
    <AnimatePresence>
      {isVisible && (
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          className="fixed inset-0 z-[9999] flex items-center justify-center pointer-events-none"
          style={{ backdropFilter: 'blur(2px)' }}
        >
          <motion.div
            initial={{ scale: 0, rotate: -180 }}
            animate={{ scale: 1, rotate: 0 }}
            exit={{ scale: 0, rotate: 180 }}
            transition={{ type: 'spring', stiffness: 200, damping: 15 }}
            className="relative pointer-events-auto"
          >
            {/* Glowing background layer */}
            <motion.div
              className={`absolute inset-0 bg-gradient-to-br ${getGradient()} rounded-2xl blur-xl opacity-75`}
              animate={{
                scale: [1, 1.1, 1],
                opacity: [0.75, 0.9, 0.75],
              }}
              transition={{
                duration: 2,
                repeat: Infinity,
                ease: "easeInOut"
              }}
            />

            {/* Main card */}
            <div className={`relative bg-gradient-to-br ${getGradient()} p-8 rounded-2xl shadow-2xl border-4 border-white/30`}>
              {/* Animated sparkle rings */}
              <motion.div
                className="absolute inset-0 rounded-2xl"
                initial={{ scale: 1, rotate: 0 }}
                animate={{ scale: 1.5, opacity: 0, rotate: 180 }}
                transition={{ duration: 1, repeat: Infinity }}
                style={{
                  border: '3px solid rgba(255, 255, 255, 0.6)',
                }}
              />

              <motion.div
                className="absolute inset-0 rounded-2xl"
                initial={{ scale: 1, rotate: 0 }}
                animate={{ scale: 1.3, opacity: 0, rotate: -180 }}
                transition={{ duration: 1, repeat: Infinity, delay: 0.3 }}
                style={{
                  border: '2px solid rgba(255, 255, 255, 0.4)',
                }}
              />

              {/* Corner sparkles */}
              {[
                { top: '-8px', left: '-8px', delay: 0 },
                { top: '-8px', right: '-8px', delay: 0.2 },
                { bottom: '-8px', left: '-8px', delay: 0.4 },
                { bottom: '-8px', right: '-8px', delay: 0.6 },
              ].map((pos, i) => (
                <motion.div
                  key={i}
                  className="absolute"
                  style={pos}
                  initial={{ scale: 0, rotate: 0 }}
                  animate={{ scale: [0, 1.5, 0], rotate: [0, 180, 360] }}
                  transition={{
                    duration: 1.5,
                    repeat: Infinity,
                    delay: pos.delay,
                  }}
                >
                  <Sparkles className="w-6 h-6 text-white drop-shadow-lg" />
                </motion.div>
              ))}

              {/* Content */}
              <div className="relative z-10 text-center">
                {/* Icon with glow effect */}
                <motion.div
                  initial={{ scale: 0 }}
                  animate={{ scale: 1 }}
                  transition={{ delay: 0.2, type: 'spring' }}
                  className="mb-4 flex justify-center relative"
                >
                  <motion.div
                    className="absolute inset-0 flex justify-center items-center"
                    animate={{
                      scale: [1, 1.2, 1],
                      opacity: [0.5, 0.8, 0.5],
                    }}
                    transition={{
                      duration: 1.5,
                      repeat: Infinity,
                      ease: "easeInOut"
                    }}
                  >
                    <div className="w-16 h-16 bg-white/30 rounded-full blur-md" />
                  </motion.div>
                  <div className="relative drop-shadow-2xl">
                    {getIcon()}
                  </div>
                </motion.div>

                <motion.h2
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ delay: 0.3 }}
                  className="text-2xl font-bold text-white mb-2 drop-shadow-lg"
                  style={{ textShadow: '0 2px 10px rgba(0,0,0,0.3)' }}
                >
                  {title}
                </motion.h2>

                <motion.p
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ delay: 0.4 }}
                  className="text-white/90 drop-shadow-md"
                  style={{ textShadow: '0 1px 5px rgba(0,0,0,0.2)' }}
                >
                  {message}
                </motion.p>

                {points && (
                  <motion.div
                    initial={{ opacity: 0, scale: 0 }}
                    animate={{ opacity: 1, scale: 1 }}
                    transition={{ delay: 0.5, type: 'spring' }}
                    className="mt-4 inline-flex items-center gap-2 bg-white/20 backdrop-blur-sm px-4 py-2 rounded-full border-2 border-white/40 shadow-lg relative overflow-hidden"
                  >
                    {/* Shimmer effect */}
                    <motion.div
                      className="absolute inset-0 bg-gradient-to-r from-transparent via-white/20 to-transparent"
                      animate={{
                        x: ['-100%', '100%'],
                      }}
                      transition={{
                        duration: 2,
                        repeat: Infinity,
                        repeatDelay: 1,
                      }}
                    />
                    <Zap className="w-5 h-5 text-yellow-300 relative z-10 drop-shadow-md" />
                    <span className="text-white font-bold relative z-10 drop-shadow-md">+{points} XP</span>
                  </motion.div>
                )}

                {/* Floating particles - enhanced */}
                {[...Array(8)].map((_, i) => {
                  const isCircle = i % 2 === 0;
                  return (
                    <motion.div
                      key={i}
                      className="absolute"
                      initial={{
                        x: 0,
                        y: 0,
                        opacity: 0,
                      }}
                      animate={{
                        x: Math.random() * 200 - 100,
                        y: Math.random() * -150 - 50,
                        opacity: [0, 1, 0],
                        rotate: isCircle ? 0 : [0, 360],
                      }}
                      transition={{
                        duration: 2.5,
                        delay: i * 0.15,
                        repeat: Infinity,
                        repeatDelay: 0.5,
                      }}
                    >
                      {isCircle ? (
                        <div className="w-3 h-3 bg-white/70 rounded-full shadow-lg" />
                      ) : (
                        <Star className="w-4 h-4 text-white/80 fill-white/60 drop-shadow-lg" />
                      )}
                    </motion.div>
                  );
                })}
              </div>
            </div>
          </motion.div>
        </motion.div>
      )}
    </AnimatePresence>
  );
};

// Utility function to trigger quick celebration without component
export const triggerQuickCelebration = (type = 'default') => {
  const configs = {
    success: {
      particleCount: 30,
      spread: 50,
      origin: { y: 0.7 },
      colors: ['#10b981', '#34d399'],
    },
    points: {
      particleCount: 20,
      spread: 30,
      origin: { y: 0.8 },
      colors: ['#fbbf24', '#f59e0b'],
      shapes: ['circle'],
    },
    streak: {
      particleCount: 40,
      spread: 60,
      origin: { y: 0.6 },
      colors: ['#ef4444', '#f87171', '#fbbf24'],
      shapes: ['star'],
    },
  };

  confetti(configs[type] || configs.success);
};

export default CelebrationAnimation;